import { DataBoxCard } from "@/components/accounting/data-box-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";

/**
 * `/accounting/settings` — our own company as it appears on the
 * documents we issue, plus the state of every number series.
 *
 * The identity can be pulled straight from the Fakturoid account, so
 * the migration doesn't start with retyping an address.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

type Settings = {
  companyName: string;
  registrationNo: string | null;
  vatNo: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string;
  vatPayer: boolean;
  standardVatRate: string;
  bankAccount: string | null;
  iban: string | null;
  swiftBic: string | null;
  defaultDueDays: number;
  invoiceNote: string | null;
  invoiceEmail: string | null;
};

type SettingsPatch = Omit<Settings, "standardVatRate"> & { standardVatRate: number };

type Series = {
  id: string;
  documentType: string;
  year: number;
  format: string;
  lastNumber: number;
};

type Response = { settings: Settings | null; series: Series[] };

export const Route = createFileRoute("/_app/accounting/settings")({
  loader: async () => {
    try {
      return await api.get<Response>("/api/accounting/settings");
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) throw redirect({ to: "/my" });
      throw err;
    }
  },
  component: SettingsPage,
});

function SettingsPage() {
  const { settings, series } = Route.useLoaderData();
  const router = useRouter();

  // The shape the API accepts — not the shape settings arrive in: the
  // rate and the due days go to the server as numbers, not as input text.
  const save = useApiMutation(
    (body: SettingsPatch) => api.patch("/api/accounting/settings", body),
    {
      onSuccess: () => router.invalidate(),
      errorMessage: () => "Couldn't save the settings.",
    },
  );

  const importFromFakturoid = useApiMutation(
    () => api.post("/api/accounting/settings/import-from-fakturoid", {}),
    {
      onSuccess: () => router.invalidate(),
      errorMessage: () => "Couldn't read the Fakturoid account.",
    },
  );

  async function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const text = (key: string) => ((fd.get(key) as string) ?? "").trim() || null;

    await save.mutate({
      companyName: (fd.get("companyName") as string).trim(),
      registrationNo: text("registrationNo"),
      vatNo: text("vatNo"),
      street: text("street"),
      city: text("city"),
      zip: text("zip"),
      country: (fd.get("country") as string).trim() || "CZ",
      vatPayer: fd.get("vatPayer") === "on",
      standardVatRate: Number(fd.get("standardVatRate")),
      bankAccount: text("bankAccount"),
      iban: text("iban"),
      swiftBic: text("swiftBic"),
      defaultDueDays: Number(fd.get("defaultDueDays")),
      invoiceNote: text("invoiceNote"),
      invoiceEmail: text("invoiceEmail"),
    });
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-semibold text-2xl">Accounting settings</h1>
          <p className="text-muted-foreground text-sm">
            What every document we issue says about us. A document already issued keeps the values
            it was issued with — changing these only affects new ones.
          </p>
        </div>
        <Button
          variant="outline"
          disabled={importFromFakturoid.pending}
          onClick={() => importFromFakturoid.mutate()}
        >
          {importFromFakturoid.pending ? "Reading…" : "Import from Fakturoid"}
        </Button>
      </div>

      {(save.error || importFromFakturoid.error) && (
        <p className="text-destructive text-sm">{save.error ?? importFromFakturoid.error}</p>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Company</CardTitle>
          <CardDescription>
            {settings
              ? "Issued documents carry these details."
              : "Nothing is set up yet — Hub cannot issue a document until it knows who is issuing it."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <Field
              name="companyName"
              label="Company name"
              required
              defaultValue={settings?.companyName ?? ""}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                name="registrationNo"
                label="Registration no."
                defaultValue={settings?.registrationNo}
              />
              <Field name="vatNo" label="VAT no." defaultValue={settings?.vatNo} />
            </div>
            <Field name="street" label="Ulice" defaultValue={settings?.street} />
            <div className="grid gap-4 sm:grid-cols-3">
              <Field name="zip" label="ZIP" defaultValue={settings?.zip} />
              <Field name="city" label="City" defaultValue={settings?.city} />
              <Field name="country" label="Country" defaultValue={settings?.country ?? "CZ"} />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <Field name="bankAccount" label="Bank account" defaultValue={settings?.bankAccount} />
              <Field name="iban" label="IBAN" defaultValue={settings?.iban} />
              <Field name="swiftBic" label="SWIFT / BIC" defaultValue={settings?.swiftBic} />
            </div>

            <div className="grid items-end gap-4 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm" htmlFor="vatPayer">
                <input
                  id="vatPayer"
                  name="vatPayer"
                  type="checkbox"
                  defaultChecked={settings?.vatPayer ?? true}
                  className="h-4 w-4"
                />
                VAT payer
              </label>
              <Field
                name="standardVatRate"
                label="Standard VAT rate (%)"
                type="number"
                defaultValue={settings ? String(Number(settings.standardVatRate)) : "21"}
              />
              <Field
                name="defaultDueDays"
                label="Due in (days)"
                type="number"
                defaultValue={String(settings?.defaultDueDays ?? 14)}
              />
            </div>

            <Field
              name="invoiceEmail"
              label="E-mail pro fakturaci"
              type="email"
              defaultValue={settings?.invoiceEmail}
            />
            <Field
              name="invoiceNote"
              label="Note on the invoice"
              defaultValue={settings?.invoiceNote}
            />

            <Button type="submit" disabled={save.pending}>
              {save.pending ? "Saving…" : "Save"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <DataBoxCard />

      <Card>
        <CardHeader>
          <CardTitle>Number series</CardTitle>
          <CardDescription>
            Each document type counts on its own, per year. A new year continues after the highest
            number already used — including the documents imported from Fakturoid.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {series.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No series yet — one is created when the first document of a type is issued.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1 pr-4 font-medium">Type</th>
                  <th className="py-1 pr-4 font-medium">Year</th>
                  <th className="py-1 pr-4 font-medium">Format</th>
                  <th className="py-1 font-medium">Last number</th>
                </tr>
              </thead>
              <tbody>
                {series.map((row) => (
                  <tr key={row.id} className="border-t">
                    <td className="py-1 pr-4">
                      <Badge variant="outline">{row.documentType}</Badge>
                    </td>
                    <td className="py-1 pr-4">{row.year}</td>
                    <td className="py-1 pr-4 font-mono text-xs">{row.format}</td>
                    <td className="py-1 tabular-nums">{row.lastNumber}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({
  name,
  label,
  defaultValue,
  required,
  type = "text",
}: {
  name: string;
  label: string;
  defaultValue?: string | null | undefined;
  required?: boolean | undefined;
  type?: string | undefined;
}) {
  return (
    <div className="space-y-2">
      <label htmlFor={name} className="font-medium text-sm leading-none">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        defaultValue={defaultValue ?? ""}
        className={INPUT_CLS}
      />
    </div>
  );
}
